import React, { useState } from "react";
import { REACTIONS } from "./constants";

// Curated, calm reaction bar (design 1c). Five fixed reactions, counts sit
// quietly, the member's own choice is highlighted; tapping toggles, choosing
// another switches. No free picker, no faces/names — counts only.
//
// Mobile (design 1f): the bar collapses to the top three reactions by count
// plus a "+N" overflow pill (S23); tapping it discloses all five. Desktop
// always shows all five. Disclosure is presentational only — no wiring.
//
// Controlled: `reactions` = { counts: {up,party,...}, my_reaction: key|null },
// `onReact(key)` toggles/switches (parent owns optimistic state + the API call).
// `disabled` blocks input while a request is in flight.
const ReactionBar = ({ reactions, onReact, disabled }) => {
  const [showAll, setShowAll] = useState(false);
  const counts = reactions?.counts || {};
  const mine = reactions?.my_reaction || null;

  // Count-descending ranking (stable sort keeps the design order on ties).
  // Both 1f artboard samples draw the collapsed pills count-descending, so the
  // mobile bar is visually reordered via flex `order`; the DOM keeps the
  // design-set order, which is what desktop (sm+) shows via `sm:order-none`.
  const ranked = [...REACTIONS].sort(
    (a, b) => (counts[b.key] || 0) - (counts[a.key] || 0),
  );
  const topKeys = ranked.slice(0, 3).map((r) => r.key);
  const overflow = REACTIONS.length - topKeys.length;
  // Literal class lookup so the Tailwind JIT scanner picks the classes up.
  const ORDER_CLASSES = ["order-1", "order-2", "order-3", "order-4", "order-5"];
  const orderClass = (key) =>
    `${ORDER_CLASSES[ranked.findIndex((r) => r.key === key)]} sm:order-none`;

  return (
    <div className="flex flex-wrap gap-2 mt-[13px] sm:mt-4">
      {REACTIONS.map((r) => {
        const isMine = mine === r.key;
        const n = counts[r.key] || 0;
        const collapsedHidden = !showAll && !topKeys.includes(r.key);
        return (
          <button
            key={r.key}
            type="button"
            title={isMine ? `${r.title} — your reaction (tap to remove)` : r.title}
            aria-pressed={isMine}
            aria-label={`${r.title}, ${n}`}
            disabled={disabled}
            onClick={() => onReact(r.key)}
            className={[
              // 42px comfort tap target on mobile, 32px pill on sm+ (design 1f/1a).
              "group items-center gap-[7px] h-[42px] px-3.5 sm:h-8 sm:px-3 rounded-full border transition",
              "disabled:opacity-60 disabled:cursor-default",
              orderClass(r.key),
              collapsedHidden ? "hidden sm:inline-flex" : "inline-flex",
              isMine
                ? "border-[#9CC6FF] bg-[#EAF3FF]"
                : "border-[#E4E9F0] bg-white hover:border-[#8FBEFC] hover:bg-[#F4F9FF] hover:shadow-[0_1px_4px_rgba(34,131,255,0.14)]",
            ].join(" ")}
          >
            <span className="text-base sm:text-[14.5px] leading-none">{r.emoji}</span>
            <span
              className={[
                "text-[13.5px] sm:text-[12.5px] tabular-nums leading-none",
                isMine
                  ? "font-extrabold text-[#1668D9]"
                  : "font-semibold text-[#5A6B82] group-hover:text-[#33455E]",
              ].join(" ")}
            >
              {n}
            </span>
          </button>
        );
      })}
      {!showAll && overflow > 0 && (
        <button
          type="button"
          aria-label="Show all reactions"
          onClick={() => setShowAll(true)}
          className="order-last sm:hidden inline-flex items-center justify-center h-[42px] px-[13px] rounded-full border border-[#E4E9F0] bg-white text-[#93A1B4] text-[13px] font-bold"
        >
          +{overflow}
        </button>
      )}
    </div>
  );
};

export default ReactionBar;
